import { type Media, type SortEnd, type WithChildren } from "@common/declarations";
import { type VideoUrlProvider } from "@common/lib/validate";
import classNames from "classnames";
import isHotkey from "is-hotkey";
import React from "react";
import { Editor, Range, Transforms } from "slate";
import { Editable, ReactEditor, type RenderElementProps, useFocused, useSelected, useSlate } from "slate-react";
import { appendEmptyLine, deleteNode, insertOrReplaceNode, isMediaNode, toggleMark } from "../lib";
import { type BlockType, type CustomElement, type ElementBase } from "../types";
import ContentElement from "./Element";
import ContentLeaf from "./Leaf";

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code"
};

export type ElementComponentProps = WithChildren & {
  attributes: any;
  availableVideoProviders?: VideoUrlProvider[];
  element: ElementBase;
  isPlaceholderShown: boolean;
  isMediaElement: boolean;
  getIndex: () => number;
  onSortEnd: (args: SortEnd) => void;
  onTextInsert: (index: number, type: BlockType) => void;
  onImageInsert: (index: number, media: Media.Item) => void;
  onVideoInsert: (index: number, media: Media.Item) => void;
};

type Props = {
  allowVideoFullScreen?: boolean;
  autoFocus?: boolean;
  availableVideoProviders?: VideoUrlProvider[];
  bunnyLibraryId?: string;
  ElementComponent?: React.ComponentType<ElementComponentProps>;
  isDisabled?: boolean;
  isReadonly?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  placeholder?: string;
  style?: React.CSSProperties;
};

const Content = ({ ElementComponent, ...props }: Props) => {
  const editor = useSlate();

  const onSortEnd = React.useCallback(
    (args: SortEnd) => {
      Transforms.moveNodes(editor, {
        at: [args.oldIndex],
        to: [args.oldIndex > args.newIndex ? args.newIndex : args.newIndex - 1]
      });
    },
    [editor]
  );

  const onImageInsert = React.useCallback(
    (index: number, media: Media.Item) => {
      const isInsertToEnd = editor.children.length === index + 1;
      insertOrReplaceNode(editor, index, {
        type: "image",
        mediaId: media.mediaId,
        url: media.sourceId,
        children: [{ text: "" }]
      });
      isInsertToEnd && appendEmptyLine(editor);
    },
    [editor]
  );

  const onVideoInsert = React.useCallback(
    (index: number, media: Media.Item) => {
      const isInsertToEnd = editor.children.length === index + 1;
      insertOrReplaceNode(editor, index, {
        type: "video",
        mediaId: media.mediaId,
        mediaSourceKey: media.mediaSourceKey,
        mediaSourceId: media.sourceId,
        aspectRatio: media.mediaSourceKey === "URL" ? "16/9" : "16/10",
        children: [{ text: "" }]
      });
      isInsertToEnd && appendEmptyLine(editor);
    },
    [editor]
  );

  const onTextInsert = React.useCallback(
    (index: number, type: BlockType) => {
      insertOrReplaceNode(editor, index, { type: type, children: [{ text: "" }] });
    },
    [editor]
  );

  const renderElement = React.useCallback(
    ({ attributes, ...renderProps }: RenderElementProps) => {
      const isRootNode = ReactEditor.findPath(editor, renderProps.element).length === 1;
      const isSelected = useSelected();
      const isFocused = useFocused();
      const isEmpty = Editor.isEmpty(editor, renderProps.element);
      const isEmptyEditor = editor.children.length === 1 && Editor.isEmpty(editor, editor.children[0] as CustomElement);

      return ElementComponent && isRootNode ? (
        <ElementComponent
          availableVideoProviders={props.availableVideoProviders}
          element={renderProps.element}
          getIndex={() => ReactEditor.findPath(editor, renderProps.element)[0]}
          attributes={attributes}
          isPlaceholderShown={
            isEmptyEditor ||
            (isFocused && isEmpty && editor.selection !== null && Range.isCollapsed(editor.selection) && isSelected)
          }
          isMediaElement={isMediaNode(renderProps.element)}
          onSortEnd={onSortEnd}
          onImageInsert={onImageInsert}
          onTextInsert={onTextInsert}
          onVideoInsert={onVideoInsert}
        >
          <ContentElement
            {...renderProps}
            allowVideoFullScreen={props.allowVideoFullScreen}
            bunnyLibraryId={props.bunnyLibraryId}
            isReadonly={props.isReadonly}
            onDelete={!props.isReadonly ? () => deleteNode(editor, renderProps.element) : undefined}
          />
        </ElementComponent>
      ) : (
        <ContentElement
          {...renderProps}
          allowVideoFullScreen={props.allowVideoFullScreen}
          attributes={attributes}
          bunnyLibraryId={props.bunnyLibraryId}
          isReadonly={props.isReadonly}
          onDelete={!props.isReadonly ? () => deleteNode(editor, renderProps.element) : undefined}
        />
      );
    },
    [ElementComponent, props.bunnyLibraryId, props.isReadonly, editor, props.placeholder]
  );

  return (
    <Editable
      autoFocus={props.autoFocus ?? false}
      className={classNames("content", { disabled: props.isDisabled })}
      onBlur={props.onBlur}
      onDragOver={(e) => (e.dataTransfer.dropEffect = "none")}
      onFocus={props.onFocus}
      onKeyDown={(event) => {
        for (const hotkey in HOTKEYS) {
          if (isHotkey(hotkey, event as any)) {
            event.preventDefault();
            const mark = HOTKEYS[hotkey];
            toggleMark(editor, mark);
          }
        }
        props.onKeyDown && props.onKeyDown(event);
      }}
      readOnly={props.isReadonly}
      placeholder={props.placeholder}
      renderPlaceholder={
        props.placeholder
          ? (data) => <div className="main_placeholder" dangerouslySetInnerHTML={{ __html: data.children }} />
          : undefined
      }
      renderElement={renderElement}
      renderLeaf={ContentLeaf}
      spellCheck
      style={props.style}
    />
  );
};
export default Content;
